import { CACHE_STATUS_FRESH, createCacheClient } from '@noths/polaris-server-caching';

import { logger } from 'src/utils/serverLogger';

const fetchPromises = {};

export type WithRedisFetchCacheArgs<T> = {
  allowCaching: boolean;
  cacheKey: string;
  cacheTTLInSeconds: number;
  fallbackOnError: T;
  fetcher: () => Promise<T>;
};

/**
 * Caches the result of an async `fetcher` in Redis: fresh hit, in-flight
 * `fetchPromises[cacheKey]` coalescing, then `.then` / `.catch`, matching
 * `fetchContentWithCaching` in `server-content-fetching`. Use for any value `T` that
 * is safe to JSON-serialize (e.g. merged sitemap lists from paged Contentstack GQL).
 */
export const withRedisFetchCache = async <T>({
  allowCaching,
  cacheKey,
  cacheTTLInSeconds,
  fallbackOnError,
  fetcher,
}: WithRedisFetchCacheArgs<T>): Promise<T> => {
  if (!allowCaching) {
    try {
      return await fetcher();
    } catch (e) {
      logger.error('withRedisFetchCache: uncached fetch failed', {
        cacheKey,
        err: e instanceof Error ? e.message : String(e),
      });
      return fallbackOnError;
    }
  }

  const cacheClient = createCacheClient();
  const cachedEntry = await cacheClient?.getByKey(cacheKey, cacheTTLInSeconds);

  if (cachedEntry && cachedEntry.status === CACHE_STATUS_FRESH) {
    return cachedEntry.data as T;
  } else {
    if (fetchPromises[cacheKey]) {
      return fetchPromises[cacheKey] as Promise<T>;
    }

    fetchPromises[cacheKey] = fetcher()
      .then((data: T) => {
        delete fetchPromises[cacheKey];

        cacheClient?.setByKey(cacheKey, data);

        return data;
      })
      .catch((e): T => {
        logger.error('withRedisFetchCache: fetch failed', {
          cacheKey,
          err: e instanceof Error ? e.message : String(e),
        });
        delete fetchPromises[cacheKey];

        if (cachedEntry?.data != null) {
          return cachedEntry.data as T;
        } else {
          return fallbackOnError;
        }
      });

    return fetchPromises[cacheKey] as Promise<T>;
  }
};
